/**
 * THE LINK TABLE — every header and footer destination, in one place.
 *
 * This file exists because of the bug the spec opens with: four mega-menu
 * items called views that never existed (go('free'), go('paths'), go('verify'),
 * go('business')) and ten footer links had no handler at all. They survived six
 * rounds of green tests because the tests asserted labels existed.
 *
 * The prototype's own fix (v21 :6570) patches the DOM after render by matching
 * a.textContent against a repair map — a band-aid that breaks the moment a
 * label is reworded. Here the destination is declared at source, and the E2E
 * gate walks THIS table: every link must resolve to a route that is either
 * built, or explicitly listed as pending with the phase that will build it.
 * A link pointing at neither is a hard failure.
 */

export type LinkStatus =
  | { kind: 'route'; to: string }
  /** Home-anchor scroll (prototype scrollHome()) — needs Home, so pending. */
  | { kind: 'anchor'; to: string; anchor: string; pendingPhase: number }
  /** Route not built yet. The phase that owns it MUST remove this. */
  | { kind: 'pending'; to: string; pendingPhase: number; note: string };

export interface NavLink {
  label: string;
  target: LinkStatus;
}

const route = (to: string): LinkStatus => ({ kind: 'route', to });
const anchor = (to: string, a: string, pendingPhase: number): LinkStatus => ({
  kind: 'anchor',
  to,
  anchor: a,
  pendingPhase,
});

/**
 * Mega-menu (v16 :6014), with v21's MENU_FIX (:6571) applied at source.
 * The four repaired items are marked below.
 */
export const MEGA_MENU: { label: string; cols: { h: string; items: NavLink[] | 'TRACKS' }[] }[] = [
  {
    label: 'Courses',
    cols: [
      {
        h: 'Browse',
        items: [
          { label: 'All courses', target: route('/courses') },
          // v16 had go('free') -> no such view. v21 fix: go('catalog','free').
          { label: 'Free courses', target: route('/courses?filter=free') },
          // v16 had go('paths') -> no such view. v21 fix: scrollHome('paths').
          { label: 'Skill paths', target: anchor('/', 'paths', 9) },
          { label: 'Course bundles', target: anchor('/', 'bundleGrid', 9) },
        ],
      },
      // Derived from the live catalog — spec 6.1: "never hardcoded".
      { h: 'By track', items: 'TRACKS' },
    ],
  },
  {
    label: 'Learn',
    cols: [
      {
        h: 'Free resources',
        items: [
          { label: 'Free notes & guides', target: route('/blog') },
          { label: 'Free downloads', target: route('/resources') },
          { label: 'Free live masterclasses', target: anchor('/', 'demos', 9) },
        ],
      },
      {
        h: 'Practice & progress',
        items: [
          { label: 'Leaderboard', target: route('/leaderboard') },
          { label: 'Our instructors', target: route('/instructors') },
          // v16 had go('verify') -> no such view. v21 fix: openVerify().
          { label: 'Verify a certificate', target: route('/verify') },
        ],
      },
    ],
  },
  {
    label: 'More',
    cols: [
      {
        h: 'MAKTech Academy',
        items: [
          { label: 'Learning centres', target: route('/centres') },
          { label: 'Book store', target: route('/store') },
          // v16 had go('business') -> no such view. v21 fix: openBusiness().
          { label: 'For business', target: route('/page/for-business') },
          { label: 'Affiliate programme', target: route('/affiliate') },
        ],
      },
    ],
  },
];

/**
 * Footer — the COMPOSED result of five blocks, not the 12 links in the base
 * markup (:558):
 *   base :558  4 Learn / 4 Company / 4 Support
 *   v2   :1862 inserts "Verify certificate" before "Help center"
 *   v3   :2190 Learn += masterclasses, CV builder; Company += For Business
 *   v11  :4529 Learn += Leaderboard
 *   v12  :4952 Learn += notes & guides, downloads, our instructors
 *   v13  :5377 Learn += book store, learning centres; Company += affiliate
 * Total 23 links.
 *
 * NOTE: spec 6.2 says "48 links", but its own enumeration lists 23 — and the
 * composed prototype yields exactly those 23. Flagged to the client; 23 is
 * what the prototype actually has.
 *
 * "Live classes" resolves to #batches, not the catalog. v2 (:1860) pointed it
 * at the live-filtered catalog via an onclick PROPERTY; v21 (:6586) re-fixed
 * it with setAttribute since getAttribute("onclick") was null, overwriting the
 * property handler. v21 runs last, so #batches wins — and spec 6.2 agrees.
 */
export const FOOTER_COLUMNS: { heading: string; links: NavLink[] }[] = [
  {
    heading: 'Learn',
    links: [
      { label: 'All courses', target: route('/courses') },
      { label: 'Free courses', target: route('/courses?filter=free') },
      { label: 'Skill paths', target: anchor('/', 'paths', 9) },
      { label: 'Live classes', target: anchor('/', 'batches', 9) },
      { label: 'Free live masterclasses', target: anchor('/', 'demos', 9) },
      { label: 'Free CV builder', target: route('/cv-builder') },
      { label: 'Leaderboard', target: route('/leaderboard') },
      { label: 'Free notes & guides', target: route('/blog') },
      { label: 'Free downloads', target: route('/resources') },
      { label: 'Our instructors', target: route('/instructors') },
      { label: 'Book store', target: route('/store') },
      { label: 'Learning centres', target: route('/centres') },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About us', target: route('/page/about-us') },
      { label: 'Become an instructor', target: route('/page/become-an-instructor') },
      { label: 'Careers', target: route('/page/careers') },
      { label: 'Contact', target: route('/page/contact') },
      { label: 'MAKTech Academy for Business', target: route('/page/for-business') },
      { label: 'Affiliate programme', target: route('/affiliate') },
    ],
  },
  {
    heading: 'Support',
    links: [
      { label: 'Verify certificate', target: route('/verify') },
      { label: 'Help center', target: route('/page/help-center') },
      { label: 'Terms of service', target: route('/page/terms-of-service') },
      { label: 'Privacy policy', target: route('/page/privacy-policy') },
      { label: 'Refund policy', target: route('/page/refund-policy') },
    ],
  },
];

/** Resolve a link target to an href. */
export function hrefOf(t: LinkStatus): string {
  if (t.kind === 'anchor') return `${t.to}#${t.anchor}`;
  return t.to;
}

/** Every link in the header + footer, flattened — the E2E gate walks this. */
export function allNavLinks(): NavLink[] {
  const out: NavLink[] = [];
  for (const m of MEGA_MENU) {
    for (const c of m.cols) {
      if (c.items !== 'TRACKS') out.push(...c.items);
    }
  }
  for (const col of FOOTER_COLUMNS) out.push(...col.links);
  return out;
}
